import { FC, useEffect, useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  HStack,
  Text,
  Badge,
  AccordionRoot,
  AccordionItem,
  AccordionItemTrigger,
  AccordionItemContent,
} from '@chakra-ui/react';
import { useAuthStore } from '@/stores/authStore';
import { useDogStore } from '@/stores/dogStore';
import { AppLayout } from '@/components/layout/AppLayout';
import { PedigreeTree } from '@/components/pedigree/PedigreeTree';
import { pedigreeService } from '@/services/firebase/pedigreeService';
import { PedigreeTree as PedigreeTreeType } from '@/types/pedigree';
import { Dog } from '@/types/dog';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { FiChevronDown } from 'react-icons/fi';

/**
 * 血統図一覧ページコンポーネント
 */
const PedigreeListPage: FC = () => {
  const { user } = useAuthStore();
  const { dogs, loading, error, fetchDogs } = useDogStore();
  const [pedigrees, setPedigrees] = useState<Map<string, PedigreeTreeType>>(new Map());
  const [loadingPedigrees, setLoadingPedigrees] = useState<Set<string>>(new Set());

  // 個体一覧を取得
  useEffect(() => {
    if (user) {
      fetchDogs(user.uid);
    }
  }, [user, fetchDogs]);

  /**
   * 血統図を取得
   */
  const fetchPedigree = async (dogId: string, dog: Dog) => {
    // 親犬情報がない場合はスキップ
    if (!dog.fatherId && !dog.motherId) {
      return;
    }

    // 既に取得済みの場合はスキップ
    if (pedigrees.has(dogId)) {
      return;
    }

    setLoadingPedigrees((prev) => new Set(prev).add(dogId));

    try {
      if (user) {
        const pedigreeData = await pedigreeService.getPedigreeTree(dogId, user.uid);
        if (pedigreeData) {
          setPedigrees((prev) => new Map(prev).set(dogId, pedigreeData));
        }
      }
    } catch (error) {
      console.error('血統図取得エラー:', error);
    } finally {
      setLoadingPedigrees((prev) => {
        const newSet = new Set(prev);
        newSet.delete(dogId);
        return newSet;
      });
    }
  };

  /**
   * 親犬情報の有無を確認
   */
  const hasPedigree = (dog: Dog): boolean => {
    return !!(dog.fatherId || dog.motherId);
  };

  // 認証ガードはルート側の ProtectedRoute が担うため、ここでは user は必ず存在する。
  if (!user) return null;

  return (
    <AppLayout>
      <Box maxW="6xl" mx="auto">
        <VStack gap={6} align="stretch">
          {/* ヘッダー */}
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <Heading size="xl">血統図一覧</Heading>
          </HStack>

          {/* ローディング表示 */}
          {loading && (
            <Box textAlign="center" py={10}>
              <Text color="gray.500">読み込み中...</Text>
            </Box>
          )}

          {/* エラー表示 */}
          {error && (
            <Box p={4} bg="red.50" borderRadius="md" borderWidth={1} borderColor="red.200">
              <Text fontSize="sm" color="red.700">
                {error}
              </Text>
            </Box>
          )}

          {/* 個体が0件の場合 */}
          {!loading && dogs.length === 0 && (
            <Box p={8} textAlign="center" bg="gray.50" borderRadius="md">
              <Text color="gray.500">個体が登録されていません</Text>
            </Box>
          )}

          {/* 個体一覧（アコーディオン形式） */}
          {!loading && dogs.length > 0 && (
            <Box bg="white" p={6} borderRadius="lg" shadow="md">
              <VStack align="stretch" gap={4}>
                <Text fontSize="sm" color="gray.600">
                  全{dogs.length}頭の個体
                </Text>

                <AccordionRoot collapsible variant="enclosed">
                  {dogs.map((dog) => (
                    <AccordionItem key={dog.id} value={dog.id}>
                      <AccordionItemTrigger
                        onClick={() => fetchPedigree(dog.id, dog)}
                        cursor="pointer"
                      >
                        <HStack justify="space-between" flex={1} pr={4}>
                          <HStack gap={4}>
                            <VStack align="start" gap={1}>
                              <HStack gap={2}>
                                <Text fontWeight="bold" fontSize="lg">
                                  {dog.name}
                                </Text>
                                <Badge
                                  colorPalette={dog.gender === 'male' ? 'blue' : 'pink'}
                                  fontSize="xs"
                                >
                                  {dog.gender === 'male' ? 'オス' : 'メス'}
                                </Badge>
                              </HStack>
                              <Text fontSize="sm" color="gray.600">
                                #{dog.bioNumber} |{' '}
                                {format(dog.birthDate.toDate(), 'yyyy年M月d日', { locale: ja })}生
                              </Text>
                            </VStack>
                          </HStack>

                          <HStack gap={2}>
                            {!hasPedigree(dog) && (
                              <Badge colorPalette="gray" fontSize="xs">
                                親犬情報なし
                              </Badge>
                            )}
                            <FiChevronDown size={20} />
                          </HStack>
                        </HStack>
                      </AccordionItemTrigger>

                      <AccordionItemContent>
                        <Box p={4}>
                          {!hasPedigree(dog) ? (
                            <Text color="gray.500" textAlign="center" py={4}>
                              この個体には親犬情報が登録されていません
                            </Text>
                          ) : loadingPedigrees.has(dog.id) ? (
                            <Text color="gray.500" textAlign="center" py={4}>
                              血統図を読み込み中...
                            </Text>
                          ) : pedigrees.has(dog.id) ? (
                            <PedigreeTree pedigree={pedigrees.get(dog.id)!} />
                          ) : (
                            <Text color="gray.500" textAlign="center" py={4}>
                              血統図を表示するには、このアイテムをクリックしてください
                            </Text>
                          )}
                        </Box>
                      </AccordionItemContent>
                    </AccordionItem>
                  ))}
                </AccordionRoot>
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>
    </AppLayout>
  );
};

export default PedigreeListPage;
